import { api } from '../../lib/api';
import type { LiveRefreshOutcome } from '../../lib/useLiveRefreshOnOpen';
import type {
  AccountWorklistResult,
  InboxMessage,
  InboxNote,
  InboxStatus,
  InboxThread,
  InboxThreadDetail,
  InboxThreadFilters,
  InboxThreadListResult,
  PostGroupResult,
  ReplyDraftsResult,
} from '../../types/inbox';

// Community Inbox Phase 1 — client API wrappers (/api/community).

function filtersToParams(
  filters: InboxThreadFilters,
  page: number,
  pageSize: number,
): URLSearchParams {
  const params = new URLSearchParams();
  const set = (key: string, values?: string[]) => {
    if (values && values.length > 0) params.set(key, values.join(','));
  };
  set('accountIds', filters.accountIds);
  set('brandAccountIds', filters.brandAccountIds);
  set('channels', filters.channels);
  set('kinds', filters.kinds);
  set('statuses', filters.statuses);
  set('assigneeUids', filters.assigneeUids);
  set('accountManagerIds', filters.accountManagerIds);
  if (filters.search && filters.search.trim() !== '') params.set('search', filters.search.trim());
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);
  if (filters.priorityOnly) params.set('priority', '1');
  if (typeof filters.minRating === 'number') params.set('minRating', String(filters.minRating));
  if (typeof filters.maxRating === 'number') params.set('maxRating', String(filters.maxRating));
  if (typeof filters.hasReply === 'boolean') params.set('hasReply', filters.hasReply ? '1' : '0');
  params.set('page', String(page));
  params.set('pageSize', String(pageSize));
  return params;
}

export async function fetchThreads(
  filters: InboxThreadFilters,
  page: number,
  pageSize: number,
  sort: 'newest' | 'oldest' = 'newest',
): Promise<InboxThreadListResult> {
  const params = filtersToParams(filters, page, pageSize);
  params.set('sort', sort);
  return api<InboxThreadListResult>(`/api/community/threads?${params.toString()}`);
}

export async function fetchThreadDetail(threadId: string): Promise<InboxThreadDetail> {
  return api<InboxThreadDetail>(`/api/community/threads/${threadId}`);
}

/** Pull this thread's conversation / post comments live from Zernio (refresh-on-open). */
export async function refreshThreadLive(threadId: string): Promise<LiveRefreshOutcome> {
  return api<LiveRefreshOutcome>(`/api/community/threads/${threadId}/refresh`, { method: 'POST' });
}

// Account worklist panel (brief §1). Sends the same filters as the thread list
// (page/pageSize omitted — the server aggregates the whole recent window).
export async function fetchAccountWorklist(
  filters: InboxThreadFilters,
): Promise<AccountWorklistResult> {
  const params = filtersToParams(filters, 1, 1);
  params.delete('page');
  params.delete('pageSize');
  return api<AccountWorklistResult>(`/api/community/account-worklist?${params.toString()}`);
}

export async function replyToThread(
  threadId: string,
  input: { message: string; isPrivate: boolean; replyToMessageId?: string },
): Promise<{ message: InboxMessage; thread: InboxThread }> {
  return api(`/api/community/threads/${threadId}/reply`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function hideComment(
  threadId: string,
  // REQUIRED — the server no longer guesses a target (see resolveTargetComment).
  messageId: string,
): Promise<{ message: InboxMessage }> {
  return api(`/api/community/threads/${threadId}/hide`, {
    method: 'POST',
    body: JSON.stringify({ messageId }),
  });
}

export async function unhideComment(
  threadId: string,
  // REQUIRED — the server no longer guesses a target (see resolveTargetComment).
  messageId: string,
): Promise<{ message: InboxMessage }> {
  return api(`/api/community/threads/${threadId}/unhide`, {
    method: 'POST',
    body: JSON.stringify({ messageId }),
  });
}

export async function deleteComment(
  threadId: string,
  // REQUIRED — the server no longer guesses a target (see resolveTargetComment).
  messageId: string,
): Promise<{ deletedMessageId: string }> {
  return api(`/api/community/threads/${threadId}/delete-comment`, {
    method: 'POST',
    body: JSON.stringify({ messageId }),
  });
}

export async function likeComment(
  threadId: string,
  messageId: string,
): Promise<{ message: InboxMessage }> {
  return api(`/api/community/threads/${threadId}/like`, {
    method: 'POST',
    body: JSON.stringify({ messageId }),
  });
}

export async function unlikeComment(
  threadId: string,
  messageId: string,
): Promise<{ message: InboxMessage }> {
  return api(`/api/community/threads/${threadId}/unlike`, {
    method: 'POST',
    body: JSON.stringify({ messageId }),
  });
}

export async function assignThread(
  threadId: string,
  assigneeUid: string | null,
): Promise<{ thread: InboxThread }> {
  return api(`/api/community/threads/${threadId}/assign`, {
    method: 'POST',
    body: JSON.stringify({ assigneeUid }),
  });
}

export async function setThreadStatus(
  threadId: string,
  status: InboxStatus,
): Promise<{ thread: InboxThread }> {
  return api(`/api/community/threads/${threadId}/status`, {
    method: 'POST',
    body: JSON.stringify({ status }),
  });
}

// Part B — the whole post's conversation for a selected comment.
export async function fetchPostGroup(threadId: string): Promise<PostGroupResult> {
  return api<PostGroupResult>(`/api/community/threads/${threadId}/post-group`);
}

// Part B — resolve every unresolved comment on this post (each audited).
export async function resolvePostComments(threadId: string): Promise<{ resolved: number }> {
  return api(`/api/community/threads/${threadId}/resolve-post`, { method: 'POST' });
}

// Part D — "resolve all showing". The preview count and the action send the
// SAME serialized filters (query string on both), so the number in the confirm
// dialog is exactly what gets resolved.
function bulkResolveParams(filters: InboxThreadFilters): string {
  const params = filtersToParams(filters, 1, 1);
  params.delete('page');
  params.delete('pageSize');
  return params.toString();
}

export async function previewBulkResolve(
  filters: InboxThreadFilters,
): Promise<{ count: number; cap: number }> {
  return api(`/api/community/threads/bulk-resolve/preview?${bulkResolveParams(filters)}`);
}

export async function bulkResolveThreads(
  filters: InboxThreadFilters,
): Promise<{ resolved: number; capped: boolean }> {
  return api(`/api/community/threads/bulk-resolve?${bulkResolveParams(filters)}`, {
    method: 'POST',
  });
}

export async function setThreadPriority(
  threadId: string,
  priority: boolean,
): Promise<{ thread: InboxThread }> {
  return api(`/api/community/threads/${threadId}/priority`, {
    method: 'POST',
    body: JSON.stringify({ priority }),
  });
}

// --- Phase 2 (Reviews) ---

export async function replyToReview(
  threadId: string,
  message: string,
): Promise<{ message: InboxMessage; thread: InboxThread }> {
  return api(`/api/community/threads/${threadId}/review-reply`, {
    method: 'POST',
    body: JSON.stringify({ message }),
  });
}

export async function deleteReviewReply(threadId: string): Promise<{ thread: InboxThread }> {
  return api(`/api/community/threads/${threadId}/delete-review-reply`, { method: 'POST' });
}

// --- Phase 2 (DMs) ---

export async function sendDmMessage(
  threadId: string,
  message: string,
): Promise<{ message: InboxMessage; thread: InboxThread }> {
  return api(`/api/community/threads/${threadId}/send-message`, {
    method: 'POST',
    body: JSON.stringify({ message }),
  });
}

/**
 * AI reply drafting. ⚠️ ON-DEMAND ONLY — call this from an explicit user action
 * (the "Suggest reply" button), NEVER from a mount effect or a poll: that would
 * generate, and bill, for every thread anyone merely looks at. Nothing is
 * stored; the drafts live only in the composer until the AM sends one through
 * the ordinary audited reply path.
 */
export async function suggestReply(
  threadId: string,
  messageId?: string,
): Promise<ReplyDraftsResult> {
  return api(`/api/community/threads/${threadId}/suggest-reply`, {
    method: 'POST',
    body: JSON.stringify({ messageId }),
  });
}

export async function addThreadNote(threadId: string, text: string): Promise<{ note: InboxNote }> {
  return api(`/api/community/threads/${threadId}/notes`, {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
}

export async function claimThread(threadId: string): Promise<{
  claimed: boolean;
  claimedByUid: string | null;
  claimedAt: string | null;
}> {
  return api(`/api/community/threads/${threadId}/claim`, { method: 'POST' });
}

export async function releaseThreadClaim(threadId: string): Promise<{ released: boolean }> {
  return api(`/api/community/threads/${threadId}/release`, { method: 'POST' });
}

// Seed a just-enabled account's recent comments (hardening Fix 2). Scoped,
// request-held, admin-only.
export async function seedAccount(
  accountId: string,
  sinceHours?: number,
): Promise<{ profilesScanned: number; threadsUpserted: number; messagesUpserted: number }> {
  const qs = sinceHours ? `?sinceHours=${sinceHours}` : '';
  return api(`/api/community/accounts/${accountId}/seed${qs}`, { method: 'POST' });
}

// ---------------------------------------------------------------------------
// Webhook admin (Phase 0c health surface — admin-only routes).
// ---------------------------------------------------------------------------

export interface CommunityWebhookHealth {
  registered: boolean;
  webhookId: string | null;
  url: string | null;
  expectedUrl: string;
  events: string[];
  isActive: boolean | null;
  failureCount: number | null;
  lastFiredAt: string | null;
  secretConfigured: boolean;
}

export async function fetchWebhookHealth(): Promise<CommunityWebhookHealth> {
  return api<CommunityWebhookHealth>('/api/community/webhook/status');
}

export async function registerWebhook(): Promise<{
  created: boolean;
  health: CommunityWebhookHealth;
}> {
  return api('/api/community/webhook/register', { method: 'POST' });
}

export async function testWebhook(): Promise<{ triggered: boolean }> {
  return api('/api/community/webhook/test', { method: 'POST' });
}
